import { Link } from "react-router-dom";
import { Crown, Zap, Eye, ThumbsUp, Play } from "lucide-react";
import { motion } from "framer-motion";
import { ScoreDonut, ClaimBadge, VerifiedBadge, LogoAvatar } from "@/components/SignalBadges";

export default function TopSpotCard({ company, index = 0 }) {
  const isPrime = company.boost_tier === "prime";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-20px" }}
      transition={{ duration: 0.48, delay: Math.min(index * 0.07, 0.28), ease: [0.16, 1, 0.3, 1] }}
      className="relative"
    >
      <Link
        to={`/company/${company.slug}`}
        data-testid={`topspot-card-${company.slug}`}
        className={`
          group relative flex flex-col sm:flex-row sm:items-stretch gap-0 rounded-2xl overflow-hidden
          transition-all duration-300
          ${isPrime
            ? "border border-amber-300/80 shadow-[0_4px_28px_rgba(251,191,36,0.18),0_2px_10px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(251,191,36,0.28),0_4px_18px_rgba(0,0,0,0.08)] hover:-translate-y-0.5"
            : "border border-slate-200 shadow-[0_2px_14px_rgba(0,0,0,0.06)] hover:shadow-[0_6px_28px_rgba(16,185,129,0.10),0_4px_16px_rgba(0,0,0,0.08)] hover:-translate-y-0.5"
          }
        `}
      >
        {/* Prime left accent bar */}
        {isPrime && (
          <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-amber-300 via-amber-400 to-amber-300/60 rounded-l-2xl" />
        )}

        {/* Card background */}
        <div className={`absolute inset-0 pointer-events-none ${
          isPrime
            ? "bg-gradient-to-br from-[#FFFBEB] via-[#FEF9EC] to-[#FFFDF6]"
            : "bg-white"
        }`} />

        {/* Prime diagonal shimmer */}
        {isPrime && (
          <div className="absolute inset-0 bg-[repeating-linear-gradient(135deg,rgba(251,191,36,0.04)_0px,rgba(251,191,36,0.04)_1px,transparent_1px,transparent_28px)] pointer-events-none" />
        )}

        {/* Main content */}
        <div className={`relative flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 p-4 sm:p-5 w-full ${isPrime ? "pl-5 sm:pl-6" : ""}`}>

          {/* Rank + logo */}
          <div className="flex items-center gap-3 shrink-0">
            <div className={`w-11 h-11 rounded-xl flex flex-col items-center justify-center shrink-0 ${
              isPrime
                ? "bg-gradient-to-br from-amber-400 to-amber-500 shadow-[0_2px_10px_rgba(245,158,11,0.35)]"
                : "bg-slate-100 border border-slate-200"
            }`}>
              {isPrime
                ? <Crown size={16} className="text-white drop-shadow-sm" />
                : <Zap size={14} className="text-emerald-600" />
              }
              <span className={`font-mono2 text-[9px] font-bold leading-none mt-0.5 ${isPrime ? "text-amber-100" : "text-slate-500"}`}>
                #{index + 1}
              </span>
            </div>
            <LogoAvatar name={company.name} logoUrl={company.logo_url} size={46} testId={`topspot-avatar-${company.slug}`} />
          </div>

          {/* Info */}
          <div className="min-w-0 flex-1">
            {/* Badges + name */}
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
              {isPrime && (
                <span className="inline-flex items-center gap-1 font-mono2 text-[8px] uppercase tracking-widest text-amber-700 bg-amber-100 border border-amber-300/60 rounded-full px-2 py-0.5">
                  <Crown size={7} /> Prime Spot
                </span>
              )}
              <ClaimBadge claimed={company.claimed} testId={`topspot-claim-${company.slug}`} />
              {company.verified && <VerifiedBadge testId={`topspot-verified-${company.slug}`} />}
              <span className={`font-heading font-bold text-base sm:text-xl leading-tight transition-colors duration-200 ${
                isPrime
                  ? "text-amber-900 group-hover:text-amber-700"
                  : "text-slate-800 group-hover:text-emerald-700"
              }`}>
                {company.name}
              </span>
            </div>

            {/* Tagline */}
            <p className={`text-xs sm:text-sm leading-snug line-clamp-1 ${
              isPrime ? "text-amber-800/70" : "text-slate-500"
            }`}>
              {company.tagline}
            </p>

            {/* Meta row */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {[company.category, company.region].map((tag) => (
                <span key={tag} className={`font-mono2 text-[8px] uppercase tracking-widest rounded px-2 py-0.5 border ${
                  isPrime
                    ? "text-amber-700 bg-amber-100/80 border-amber-200"
                    : "text-slate-500 bg-slate-100 border-slate-200"
                }`}>
                  {tag}
                </span>
              ))}
              <span className={`font-mono2 text-[9px] inline-flex items-center gap-1 ${isPrime ? "text-amber-600/70" : "text-slate-400"}`}>
                <ThumbsUp size={9} /> {company.upvotes || 0}
              </span>
              <span className={`font-mono2 text-[9px] inline-flex items-center gap-1 ${isPrime ? "text-amber-600/70" : "text-slate-400"}`}>
                <Eye size={9} /> {company.views || 0}
              </span>
            </div>
          </div>

          {/* Media slot */}
          <div
            data-testid={`topspot-media-${company.slug}`}
            className={`shrink-0 w-full sm:w-36 lg:w-44 aspect-video rounded-xl overflow-hidden border ${
              isPrime ? "border-amber-200" : "border-slate-200"
            }`}
          >
            {company.slot_video_url ? (
              <video autoPlay muted loop playsInline preload="auto" className="w-full h-full object-cover">
                {company.slot_video_webm && <source src={company.slot_video_webm} type="video/webm" />}
                <source src={company.slot_video_url} type="video/mp4" />
              </video>
            ) : company.gif_url || company.logo_url ? (
              <img src={company.gif_url || company.logo_url} alt={`${company.name} media`} className="w-full h-full object-cover" />
            ) : (
              <div className={`w-full h-full flex flex-col items-center justify-center gap-1.5 ${
                isPrime ? "bg-amber-50" : "bg-slate-50"
              }`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isPrime ? "bg-amber-100 text-amber-500" : "bg-slate-100 text-slate-400"
                }`}>
                  <Play size={13} />
                </span>
                <span className={`font-mono2 text-[8px] uppercase tracking-widest ${isPrime ? "text-amber-500/80" : "text-slate-400"}`}>
                  Media slot
                </span>
              </div>
            )}
          </div>

          {/* Score */}
          <div className="flex flex-row sm:flex-col items-center sm:items-center justify-between sm:justify-start gap-1 shrink-0 sm:pr-1">
            <ScoreDonut score={company.highrank_score} size={48} testId={`topspot-score-${company.slug}`} />
            <span className={`font-mono2 text-[8px] uppercase tracking-widest text-center ${isPrime ? "text-amber-600/60" : "text-slate-400"}`}>
              Score
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
